import { graphql, useQuery } from "@opensea/graphql"
import type { AvatarProps } from "@opensea/ui-kit"
import {
  classNames,
  Dropdown,
  Flex,
  FlexColumn,
  formatAddress,
  Media,
  SideSheetHeaderTitle,
  SkeletonCircle,
  SkeletonLine,
  TextBody,
  UnstyledButton,
  useIsLessThanLg,
  withSuspense,
} from "@opensea/ui-kit"
import { ExpandMore } from "@opensea/ui-kit/icons"
import { useState } from "react"
import { Avatar } from "../../../../components/Avatar"
import { useAccount, useConnectors } from "../../../../polychain/hooks"
import type { Address } from "../../../../types"
import { ProfileDropdownContent } from "./ProfileDropdownContent"

export const ViewWalletHeaderProfileSectionQuery = graphql(`
  query ViewWalletHeaderProfileSectionQuery($address: Address!) {
    profileByAddress(address: $address) {
      __typename
      ... on ProfileIdentifier {
        displayName
        address
        imageUrl
      }
    }
  }
`)

type ViewWalletHeaderProfileSectionProps = {
  address: Address
}

export const ViewWalletHeaderProfileSection = withSuspense(
  ({ address }: ViewWalletHeaderProfileSectionProps) => {
    const [{ data }] = useQuery({
      query: ViewWalletHeaderProfileSectionQuery,
      variables: {
        address,
      },
    })
    const profile = data?.profileByAddress

    const { connectorId } = useAccount()
    const { getConnector } = useConnectors()
    const renderBadge = () => {
      if (!connectorId) {
        return
      }
      const connector = getConnector(connectorId)
      if (!connector) {
        return
      }
      return (
        <Avatar.Badge>
          <img className="size-4" src={connector.icon} />
        </Avatar.Badge>
      )
    }

    const [isDropdownOpen, setIsDropdownOpen] = useState(false)

    const isLessThanLg = useIsLessThanLg()

    if (profile?.__typename === "BannedProfile") {
      return null
    }

    const hasProfileDisplayName = !!profile?.displayName
    const displayName = getDisplayName(profile?.displayName, address)
    const avatarProps: AvatarProps = {
      badge: renderBadge(),
      rounded: true,
      ...(profile?.imageUrl ? { src: profile.imageUrl } : { seed: address }),
    }

    return (
      <Dropdown
        align={isLessThanLg ? "center" : "start"}
        content={<ProfileDropdownContent address={address} />}
        onOpenChange={setIsDropdownOpen}
        overrides={{
          Content: { className: "min-w-[300px] max-w-[360px]" },
        }}
        size="md"
      >
        <UnstyledButton className="flex cursor-pointer items-center gap-4">
          <Media greaterThanOrEqual="lg">
            <Avatar {...avatarProps} size={48} />
          </Media>
          <Media lessThan="lg">
            <Avatar {...avatarProps} size={36} />
          </Media>
          <FlexColumn className="items-start lg:gap-0.5">
            <SideSheetHeaderTitle className="text-sm lg:text-heading-sm">
              {/* TODO(jihok): handle overflow for really long names */}
              {displayName}
              &nbsp;
              <ExpandMore
                aria-hidden={true}
                className={classNames(
                  "inline transition-transform",
                  isDropdownOpen && "rotate-180",
                )}
              />
            </SideSheetHeaderTitle>
            {hasProfileDisplayName && (
              <TextBody color="text-secondary" size="xs">
                {formatAddress(profile.address)}
              </TextBody>
            )}
          </FlexColumn>
        </UnstyledButton>
      </Dropdown>
    )
  },
  ViewWalletHeaderProfileSectionSkeleton,
)

export function ViewWalletHeaderProfileSectionSkeleton() {
  return (
    <Flex className="items-center gap-4">
      <SkeletonCircle className="size-9 lg:size-12" />
      <FlexColumn className="gap-0.5">
        <SideSheetHeaderTitle>
          <SkeletonLine className="my-0.5 h-5 w-[180px]" />
        </SideSheetHeaderTitle>
        <TextBody size="xs">
          <SkeletonLine className="my-[3px] h-3 w-[90px]" />
        </TextBody>
      </FlexColumn>
    </Flex>
  )
}

function getDisplayName(
  profileDisplayName: string | null | undefined,
  address: Address,
) {
  if (profileDisplayName) {
    return profileDisplayName
  }
  return formatAddress(address)
}
